import React, { useState, useEffect } from "react";
import {
  FaEnvelope,
  FaInstagram,
  FaPaperPlane,
  FaQuestionCircle,
  FaArrowRight,
} from "react-icons/fa";
import { toast } from "sonner";
import { sendContactForm } from "../../api/services/contact";

import MainContainer from "../../components/MainContainer/MainContainer";
import SEO from "../../components/SEO/SEO";
import {
  Container,
  ContactHeader,
  ContactContent,
  ContactInfo,
  ContactItem,
  ContactForm,
  FormGroup,
  Input,
  TextArea,
  SubmitButton,
  FAQCard,
} from "./ContactPage.styles";

const ContactPage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "", // honeypot — skriveno polje, popunjavaju ga samo botovi
  });

  // Check if business is open (Mon-Fri 9:00-18:00, Sat 10:00-16:00, Belgrade time)
  const checkBusinessHours = () => {
    const now = new Date();
    const belgradeTZ = "Europe/Belgrade";
    const belgradeTime = new Date(
      now.toLocaleString("en-US", { timeZone: belgradeTZ }),
    );

    const day = belgradeTime.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const hour = belgradeTime.getHours();

    // Monday-Friday 9:00-18:00
    if (day >= 1 && day <= 5) {
      return hour >= 9 && hour < 18;
    }
    // Saturday 10:00-16:00
    if (day === 6) {
      return hour >= 10 && hour < 16;
    }
    // Sunday - closed
    return false;
  };

  useEffect(() => {
    // Check business hours on component mount
    setIsOpen(checkBusinessHours());

    // Update every minute
    const interval = setInterval(() => {
      setIsOpen(checkBusinessHours());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: "Email",
      info: "info@mitovka.com",
      link: "mailto:info@mitovka.com",
    },
    {
      icon: <FaInstagram />,
      title: "Instagram",
      info: "@mm_beautyspot",
      link: "https://www.instagram.com/mm_beautyspot/",
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Molimo popunite sva obavezna polja");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await sendContactForm(formData);
      toast.success(
        response.message ||
          "Poruka je uspešno poslata! Odgovoriću vam u najkraćem roku.",
      );

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        website: "",
      });
    } catch (error) {
      toast.error(
        error.message || "Greška prilikom slanja poruke. Pokušajte ponovo.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainContainer>
      <SEO
        title="Kontakt"
        description="Kontaktirajte MITOVKA BeautySpot — pitanja o online edukacijama, plaćanju, pristupu kursevima i sertifikatima."
        path="/kontakt"
      />

      <Container>
        <ContactHeader>
          <h1>Kontakt</h1>
          <p>
            Stojim vam na raspolaganju za sva dodatna pitanja i informacije.
          </p>
        </ContactHeader>

        <ContactContent>
          <ContactInfo>
            <h2>Stupite u Kontakt</h2>
            <p>
              Bilo da imate pitanja o kursevima, želite da zakažete konsultaciju
              ili jednostavno želite da saznate više o mojim uslugama, slobodno
              me kontaktirajte.
            </p>

            {contactInfo.map((item, index) => (
              <ContactItem key={index}>
                <div className="icon">{item.icon}</div>
                <div className="info">
                  <h4>{item.title}</h4>
                  <p>
                    {item.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {item.info}
                      </a>
                    ) : (
                      item.info
                    )}
                  </p>
                  {item.title === "Email" && (
                    <div className="business-hours">
                      <div
                        className={`status-indicator ${
                          isOpen ? "open" : "closed"
                        }`}
                      ></div>
                      <span className="status-text">
                        {isOpen
                          ? "Trenutno dostupni - očekujte brz odgovor"
                          : "Van radnog vremena - odgovaramo u toku radnog dana"}
                      </span>
                    </div>
                  )}
                </div>
              </ContactItem>
            ))}

            <FAQCard href="/cesta-pitanja">
              <FaQuestionCircle />
              <span>Česta pitanja</span>
              <FaArrowRight />
            </FAQCard>
          </ContactInfo>

          <ContactForm onSubmit={handleSubmit}>
            <h3>Pošaljite Poruku</h3>

            {/* Honeypot — nevidljivo za ljude, botovi ga popune i bivaju odbijeni */}
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                width: "1px",
                height: "1px",
                opacity: 0,
              }}
            />

            <FormGroup>
              <label htmlFor="name">
                Ime i Prezime <span className="required">*</span>
              </label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Vaše ime i prezime"
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="email">
                Email Adresa <span className="required">*</span>
              </label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="vasa.email@adresa.com"
                required
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="phone">Broj Telefona</label>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+381 69 123 4567"
              />
            </FormGroup>

            <FormGroup>
              <label htmlFor="message">
                Poruka <span className="required">*</span>
              </label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Opišite kako mogu da vam pomognem..."
                required
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <l-line-spinner size="16" stroke="2" speed="1" color="#fff" />
                  Šalje se...
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  Pošalji Poruku
                </>
              )}
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </Container>
    </MainContainer>
  );
};

export default ContactPage;
